import { useEffect, type ReactNode } from 'react';

/** The modal sits at z-[60]; the shared tooltip defaults to z-50, under it. */
export const TOOLTIP_OVER_MODAL = '!z-[70]';

interface ModalShellProps {
    /** e.g. 'max-w-[460px]'. */
    widthClassName: string;
    /** Escape and overlay-click are both disabled while a save is in flight. */
    dismissible: boolean;
    onDismiss: () => void;
    /** id of the panel's heading, for aria-labelledby. */
    labelledBy: string;
    /** Panel radius, padding and scroll behaviour; replaces the default. */
    panelClassName?: string;
    children: ReactNode;
}

// Dialog contract for the billing modals: role, aria-modal, Escape and
// overlay-click, matching ConfirmModalShell which the rest of the app uses.
export function ModalShell({
    widthClassName,
    dismissible,
    onDismiss,
    labelledBy,
    panelClassName = 'rounded-xl p-6',
    children
}: ModalShellProps) {
    useEffect(() => {
        if (!dismissible) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onDismiss();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [dismissible, onDismiss]);

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex justify-center items-center p-6"
            onClick={dismissible ? onDismiss : undefined}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={labelledBy}
                className={`bg-white shadow-xl w-full ${widthClassName} ${panelClassName}`}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );
}
